import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useLang } from "../i18n";
import api from "../lib/api";
import { PrimaryButton } from "../components/common";
import { Input } from "../components/ui/input";
import { Home, Loader2, ShieldCheck, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    setErr("");
    if (pw.length < 6) { setErr(t("password_min")); return; }
    if (pw !== pw2) { setErr(t("password_mismatch")); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password: pw });
      setDone(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Something went wrong");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-sand max-w-md mx-auto px-5 py-10 flex flex-col justify-center">
      <div className="flex items-center gap-2 mb-6 gs-rise">
        <div className="w-11 h-11 rounded-xl bg-terracotta flex items-center justify-center text-white"><Home className="w-6 h-6" strokeWidth={2.5} /></div>
        <div className="font-head font-extrabold text-2xl text-slate-900">{t("app_name")}</div>
      </div>

      <div className="gs-card p-5 gs-rise">
        {(() => {
          if (done) return (
          <div className="text-center py-4" data-testid="reset-success">
            <div className="mx-auto w-14 h-14 rounded-full bg-trust-light flex items-center justify-center mb-3"><CheckCircle2 className="w-7 h-7 text-trust" /></div>
            <p className="font-head font-bold text-lg text-slate-900">{t("reset_success")}</p>
            <PrimaryButton onClick={() => navigate("/")} data-testid="goto-login-btn" className="mt-5">{t("login")}</PrimaryButton>
          </div>
          );
          if (!token) return (
          <div className="text-center py-4">
            <p className="text-sm text-red-500" data-testid="reset-no-token">This reset link is invalid. Please request a new one.</p>
            <PrimaryButton onClick={() => navigate("/")} className="mt-5">{t("back_to_login")}</PrimaryButton>
          </div>
          );
          return (
          <>
            <h1 className="font-head text-xl font-bold text-slate-900 mb-1">{t("reset_password")}</h1>
            <p className="text-sm text-slate-500 mb-4">{t("reset_desc")}</p>
            <Input type="password" className="gs-tap mb-3 rounded-xl" placeholder={t("new_password")} data-testid="reset-new-password" value={pw} onChange={(e) => setPw(e.target.value)} />
            <Input type="password" className="gs-tap mb-4 rounded-xl" placeholder={t("confirm_password")} data-testid="reset-confirm-password" value={pw2} onChange={(e) => setPw2(e.target.value)} />
            {err && <p className="text-sm text-red-500 mb-3" data-testid="reset-error">{err}</p>}
            <PrimaryButton onClick={submit} disabled={busy} data-testid="reset-submit-btn">
              {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : t("update_password")}
            </PrimaryButton>
          </>
          );
        })()}
        <div className="flex items-center gap-1.5 justify-center mt-4 text-xs text-slate-400">
          <ShieldCheck className="w-3.5 h-3.5 text-trust" /> {t("encrypted_note")}
        </div>
      </div>
    </div>
  );
}
